import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTemplatesList } from '@/hooks/useTemplates';
import type { Template } from '@/types/api.types';

const CONTACT_FIELDS: { value: string; label: string }[] = [
  { value: '{{name}}', label: 'Nome do Contato' },
  { value: '{{phone}}', label: 'Telefone' },
  { value: '{{documento}}', label: 'Documento (CNPJ/CPF)' },
  { value: '{{razaoSocial}}', label: 'Razão Social' },
  { value: '{{nomeFantasia}}', label: 'Nome Fantasia' },
  { value: '{{email}}', label: 'E-mail' },
];

export interface Step1Data {
  name: string;
  templateId: string;
  template: Template | null;
  fieldMappings: Record<string, string>;
}

interface Props {
  data: Step1Data;
  onChange: (patch: Partial<Step1Data>) => void;
  onNext: () => void;
}

function getVariables(template: Template): string[] {
  if (!Array.isArray(template.variables)) return [];
  return template.variables as string[];
}

export function Step1Template({ data, onChange, onNext }: Props) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { data: tplData } = useTemplatesList({ isActive: true, type: 'template' });
  const templates = tplData?.data ?? [];

  const variables = data.template ? getVariables(data.template) : [];

  function handleTemplateChange(id: string) {
    const tpl = templates.find((t) => t.id === id) ?? null;
    onChange({ templateId: id, template: tpl, fieldMappings: {} });
  }

  function handleNext() {
    const errs: Record<string, string> = {};
    if (!data.name.trim()) errs.name = 'Nome da campanha é obrigatório';
    if (!data.templateId) errs.template = 'Selecione um template';
    for (const v of variables) {
      if (!data.fieldMappings[v]?.trim()) errs[v] = `Mapeamento do campo "${v}" é obrigatório`;
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    onNext();
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="campaign-name">Nome da campanha *</Label>
        <Input
          id="campaign-name"
          placeholder="Black Friday 2025"
          value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
          className={errors.name ? 'border-destructive' : ''}
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="space-y-2">
        <Label>Template de mensagem *</Label>
        <Select value={data.templateId} onValueChange={handleTemplateChange}>
          <SelectTrigger className={errors.template ? 'border-destructive' : ''}>
            <SelectValue placeholder="Selecionar template…" />
          </SelectTrigger>
          <SelectContent>
            {templates.length === 0 && (
              <SelectItem value="__none__" disabled>
                Nenhum template HSM ativo — sincronize da Zenvia
              </SelectItem>
            )}
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.template && <p className="text-xs text-destructive">{errors.template}</p>}
      </div>

      {variables.length > 0 && (
        <div className="space-y-2">
          <Label>Variáveis do template</Label>
          <p className="text-xs text-muted-foreground">
            Mapeie cada variável para um campo do contato.
          </p>
          <div className="rounded-md border divide-y">
            {variables.map((v) => (
              <div key={v} className="flex items-center gap-3 px-3 py-2">
                <span className="w-36 shrink-0 font-mono text-sm text-muted-foreground">
                  {`{{${v}}}`}
                </span>
                <span className="text-muted-foreground">→</span>
                <Select
                  value={data.fieldMappings[v] ?? ''}
                  onValueChange={(val) =>
                    onChange({ fieldMappings: { ...data.fieldMappings, [v]: val } })
                  }
                >
                  <SelectTrigger className={`h-8 flex-1 ${errors[v] ? 'border-destructive' : ''}`}>
                    <SelectValue placeholder="Selecionar campo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_FIELDS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors[v] && <p className="text-xs text-destructive shrink-0">{errors[v]}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleNext}>Próximo →</Button>
      </div>
    </div>
  );
}
